/** What every screen needs: the workspace, the Scope 2 method, and the drawer's URL state.
 *
 * Extracted because all five screens need the same four things, and because the drawer's state
 * lives in the URL — so "open a receipt" is a navigation, not a `setState`. Doing that inline on
 * five screens would mean five slightly different implementations of the same URL contract.
 */

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";

import type { Membership, Organization, Scope2Method } from "../api/queries";
import { apiFetch } from "../api/client";
import { useMemberships, useOrganization } from "../api/queries";
import { markSwitchPending } from "../api/switchPending";
import { shortDate, windowRange } from "../lib/format";
import type { ScreenSearch } from "./search";

/** The reporting window every figure on a screen is for.
 *
 * Dates rather than an id, because a URL carrying `from=2025-04-01&to=2026-03-31` says what it
 * means and resolves without a lookup — and because a period id belongs to one organisation, which
 * a shareable link must not assume.
 */
export interface Window {
  period_start: string;
  period_end: string;
  /** What to call it on screen: "All entities · Apr 2025 to Mar 2026", or for a workspace with one
   * company "Sierravale Foods California, Inc. · FY2026". It names what the figures sum. */
  label: string;
  /** The declared period's own name for the year ("FY2025-26"), where the window is one: the title's
   * year word and a trend's step names are read from it, since `label` no longer carries it. */
  name?: string;
}

export interface Screen {
  /** Every organisation this person may work in, for the switcher. A name and a role, no data.
   *
   * **Empty while the read is in flight and when it failed, and those are not the same claim** —
   * so `membershipsError` travels beside it. An empty list on its own says *"you work in one
   * workspace"*, which is what a dropped `/memberships` used to say: the switcher simply
   * disappeared for a person in two workspaces, with no sentence saying a read had failed. */
  memberships: readonly Membership[];
  /** Why the list of workspaces could not be read, or `null`. The frame renders it with the shared
   * `ReadFailed`, so this is the one way a failed `/memberships` is shown. */
  membershipsError: Error | null;
  /** Ask for the list of workspaces again. */
  retryMemberships: () => void;
  organization: Organization | undefined;
  orgId: string;
  method: Scope2Method;
  /** The window every figure is for (F11). Never a constant — see `resolveWindow`.
   *
   * **`undefined` is a fact, not a placeholder**: no window is in force because the URL names none
   * and the workspace has declared no reporting period (or has not loaded yet, which `isLoading`
   * says). It used to be a 1970 date that looked like data and was sent to the API as if it were,
   * so the type now makes every caller say what it does without one. The data hooks in
   * `api/queries.ts` take `Window | undefined` and **do not issue** a request for it. */
  window: Window | undefined;
  /** Whether the workspace has declared at least one reporting period — the first-run question.
   *
   * Not the same as `window !== undefined`: a URL can name dates no period covers (EXP-21), and
   * that is a window with nothing declared behind it. Meaningful once `organization` has loaded;
   * `ScreenFrame` renders no screen before that, so no screen reads it earlier. */
  hasPeriod: boolean;
  /** Every window the selector may offer: the workspace's own, **plus the one in force** when a
   * link named dates no reporting period covers (EXP-21's arbitrary-period clause).
   *
   * The second half is not a convenience. The selector is a `<select>` whose `value` is the window
   * in force, and an HTML select whose value matches none of its options renders with **nothing
   * chosen** — so `?from=2025-01-01&to=2025-06-30` produced a blank period control above figures
   * that were for that range. The top bar's whole job in this shell is to say what the figures
   * below are for, and a blank one says nothing while looking like a control somebody can read.
   */
  windows: readonly Window[];
  setWindow: (period: Window) => void;
  receipt: string | undefined;
  /** The receipt node to land on, from the URL (`source`, `verifier`, `factor`, `ledger`). */
  receiptPart: string | undefined;
  breakdown: string | undefined;
  /** Which tab of the record page, from the URL, so a tab is linkable. */
  tab: string | undefined;
  setTab: (tab: string) => void;
  /** Which filing obligation the disclosure screens are about, from the URL (UX-01).
   *
   * Handed over **unvalidated**, because only the screen holds the list it must be checked
   * against: `search.ts` can say this is a string and cannot say it names a filing this workspace
   * has. The screens apply the register's rule — a value that is not on offer is not in force.
   */
  filing: string | undefined;
  setFiling: (obligationId: string) => void;
  isLoading: boolean;
  error: Error | null;
  /** Ask for the workspace again. **Every screen's top-level failure had no way out but a page
   * reload**, which is the reader being told to turn it off and on again — and on a screen whose
   * whole job is to be trusted about numbers, a dead end reads as the product being broken rather
   * than one request having failed. */
  retry: () => void;
  setOrganization: (id: string) => void;
  setMethod: (method: Scope2Method) => void;
  /** `part` names the receipt node to land on (an evidence-meter click). */
  openReceipt: (calculationId: string, part?: string) => void;
  openBreakdown: (key: string) => void;
  closeDrawer: () => void;
}

/** Move to another organisation, then land on a clean page.
 *
 * A full reload rather than a cache invalidation: the session has changed, every query in flight
 * belongs to the organisation being left, and the surest way to have none of them land is not to be
 * on that page any more. It happens rarely enough that the cost does not matter.
 */
export async function switchOrganisation(orgId: string, workspace: string): Promise<void> {
  const response = await apiFetch("/api/v1/auth/switch", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ org_id: orgId }),
  });
  if (!response.ok) return;
  // **A 200 is not always a signed-in session** (G-P2-02). Moving into a workspace where the role
  // needs a second factor revokes the old session and leaves a half-session in the cookie, and the
  // body says so. Reloading with that unsaid dropped the person on the bare credentials form, which
  // has no code box, while the server waited for a code. Say it to the sign-in screen, which opens
  // on the step the server is waiting for.
  const body: unknown = await response.json().catch(() => null);
  if (
    body &&
    typeof body === "object" &&
    "mfa_required" in body &&
    body.mfa_required === true
  ) {
    markSwitchPending({
      enrolmentRequired: "mfa_enrolment_required" in body && body.mfa_enrolment_required === true,
      workspace,
    });
  }
  window.location.assign("/");
}

export function useScreen(): Screen {
  // `Partial<ScreenSearch>` rather than a second list of the same keys: `search.ts` owns the URL
  // contract, and a contract written down twice is one that eventually disagrees with itself.
  const search: Partial<ScreenSearch> = useSearch({ strict: false });
  const navigate = useNavigate();
  const { data: organization, isPending, error, refetch } = useOrganization();
  const membershipsRead = useMemberships();

  // No `?org=` any more, and the search parameter is ignored rather than honoured. The session
  // decides which organisation this is; a URL that could choose one would be an authorisation
  // decision made from attacker-controlled input, and the API would refuse it anyway.

  // The basis is the address's: `search.ts` fills it from "Show market-based Scope 2 everywhere"
  // (My settings > Advanced) when the address names none, so the preference reaches every screen
  // through this one value (G-Wviewer-04), and an explicit `?method=` still wins.
  const method: Scope2Method = search.method === "market" ? "market" : "location";
  const declared = declaredWindows(organization);
  const window = resolveWindow(declared, search.from, search.to);
  // The window in force is always selectable, so the control and the figures cannot disagree. It
  // goes **first**: it is what is being shown, and a reader looks at the top of a list.
  const windows =
    !window ||
    declared.some(
      (candidate) =>
        candidate.period_start === window.period_start &&
        candidate.period_end === window.period_end,
    )
      ? declared
      : [window, ...declared];

  const patch = useCallback(
    (next: Record<string, string | undefined>) => {
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, ...next }),
        replace: true,
      });
    },
    [navigate],
  );

  return {
    // `?? []` is only the swallow when nothing else carries the failure; `membershipsError` does.
    memberships: membershipsRead.data ?? [],
    membershipsError: membershipsRead.error,
    retryMemberships: () => void membershipsRead.refetch(),
    organization,
    orgId: organization?.id ?? "",
    method,
    window,
    hasPeriod: declared.length > 0,
    windows,
    receipt: search.receipt,
    receiptPart: search.part,
    breakdown: search.breakdown,
    tab: search.tab,
    filing: search.filing,
    isLoading: isPending,
    error: error,
    retry: () => void refetch(),
    setOrganization: (id) => {
      // Switching workspace is a **new session**, not a query parameter — so it is a request, and
      // the page reloads afterwards. The drawer state goes with it: a receipt id belongs to one
      // organisation, and carrying it across would ask the API for another tenant's figure.
      void switchOrganisation(
        id,
        membershipsRead.data?.find((membership) => membership.org_id === id)?.legal_name ?? "",
      );
    },
    setMethod: (next) => patch({ method: next }),
    setWindow: (next) => {
      // The drawer closes with the window. A receipt belongs to a figure in one period, and a
      // drawer left open across a change of period shows last year's number under this year's
      // heading — which is the stale-figure failure ADR-0027 names, at its most confusing.
      patch({
        from: next.period_start,
        to: next.period_end,
        receipt: undefined,
        part: undefined,
        breakdown: undefined,
      });
    },
    // A tab change is `replace: true` like every other patch here, so five tabs do not become five
    // back-button presses on the way out of a record.
    setTab: (next) => patch({ tab: next }),
    // `replace: true` for the tab's reason: three filings compared in turn must not cost three
    // Back presses to leave the screen. What Back has to restore is the filing a reader **left**
    // the screen on, and that is what this gives — the address bar carries it, so returning from a
    // record arrives on the filing it was opened from rather than on whichever one sorts first.
    setFiling: (next) => patch({ filing: next }),
    openReceipt: (calculationId, part) => patch({ receipt: calculationId, part }),
    openBreakdown: (key) => patch({ breakdown: key, receipt: undefined, part: undefined }),
    closeDrawer: () => patch({ receipt: undefined, part: undefined, breakdown: undefined }),
  };
}


/** Every reporting window this workspace has, newest first, deduplicated by dates.
 *
 * From `organization.periods`, which are keyed to a **(regime, legal entity)** pair — so a group
 * with a UK parent and a Californian subsidiary has two, with different year ends, and that is the
 * case this selector exists for. Two regimes over identical dates are one window here: a reader
 * picking a period is picking dates, not a filing.
 */
export function declaredWindows(organization: Organization | undefined): Window[] {
  const periods = organization?.periods ?? [];
  // **The label says what is summed.** `GET /inventory` and every figure under a window add up every
  // company's activity inside its dates, whichever company declared the year. A label naming one
  // company beside a total that includes another is the claim Home made (G-Wviewer-01): so with more
  // than one company in the workspace every window is "All entities", and its dates are the one
  // thing all of them share. With one company the company and its own name for the year are true and
  // stay. (The window opened by default is D-07 and is not decided here.)
  const entities = new Set(periods.map((period) => period.legal_entity_name));
  const seen = new Map<string, Window>();
  for (const period of periods) {
    const key = `${period.start_date}|${period.end_date}`;
    if (!seen.has(key)) {
      seen.set(key, {
        period_start: period.start_date,
        period_end: period.end_date,
        label:
          entities.size > 1
            ? `${ALL_ENTITIES} · ${windowRange(period.start_date, period.end_date)}`
            : `${period.legal_entity_name} · ${period.label}`,
        name: period.label,
      });
    }
  }
  return [...seen.values()].sort((a, b) => b.period_start.localeCompare(a.period_start));
}

/** What a window of a workspace with several companies is called: the figures are all of them. */
export const ALL_ENTITIES = "All entities";

/** A window somebody asked for that is none of the workspace's own — EXP-21's arbitrary period.
 *
 * **Named as what it is, and dated in words.** The label used to be `` `${from} to ${to}` ``,
 * which puts `2025-01-01 to 2025-06-30` in the one place on every screen that says what the
 * figures below are for. ISO is at least unambiguous, which is why it was not a wrong date — but
 * `30-design-principles.md` rule 7 says no date this product shows is written in digits alone, and
 * `shortDate` is the function that keeps it. The suffix matters as much: without it a hand-made
 * range sits in the selector looking exactly like a declared reporting period, and a reader
 * comparing two of them has no way to tell a filing window from a question somebody asked.
 */
export function customWindow(from: string, to: string): Window {
  return {
    period_start: from,
    period_end: to,
    label: `${shortDate(from)} – ${shortDate(to)} · custom range`,
  };
}

/** The window in force: the URL's if it names one, otherwise the most recent the workspace has —
 * and **`undefined` when it has neither**.
 *
 * **There is no hard-coded year** (finding F11). Every main query used a `WINDOW` constant of
 * calendar 2025, so a March year-end could not be looked at and a second annual cycle needed a
 * source edit. A URL that names dates is honoured as given — including dates no period covers,
 * because a reader comparing a custom window is doing something legitimate and the API clamps them
 * to their engagement anyway.
 *
 * **What this used to return for a workspace with no periods was a 1970 date**, with a comment
 * saying it only existed before the organisation loaded and that every query using it was
 * disabled or refetched the moment it did. Neither half was true: nothing disabled anything, and
 * the organisation loading changes nothing when the answer is "no periods". So a brand-new
 * workspace sent `/inventory?period_start=1970-01-01…` and `/figures?…` and got 200s, the top bar
 * said "no reporting period" as though it were a period, and every navigation link copied the
 * sentinel into the address bar, where this function read it back as a custom range somebody had
 * asked for. A placeholder that looks like data becomes data. This returns nothing instead, and
 * the compiler finds every caller that assumed otherwise.
 */
function resolveWindow(windows: readonly Window[], from?: string, to?: string): Window | undefined {
  if (from && to) {
    return (
      windows.find((w) => w.period_start === from && w.period_end === to) ?? customWindow(from, to)
    );
  }
  return windows[0];
}
